import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { api, jsonBody, setCsrfToken } from './api'
import type { SessionResponse, TenantRole } from './types'

interface AuthContextValue {
  session: SessionResponse | null
  loading: boolean
  role: TenantRole | null
  refresh: () => Promise<void>
  establish: (session: SessionResponse) => void
  logout: () => Promise<void>
  switchTenant: (tenantId: string) => Promise<void>
  exitPlatformAccess: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionResponse | null>(null)
  const [loading, setLoading] = useState(true)

  const establish = useCallback((next: SessionResponse) => {
    setCsrfToken(next.csrfToken)
    setSession(next)
  }, [])

  const refresh = useCallback(async () => {
    try {
      establish(await api<SessionResponse>('/auth/me'))
    } catch {
      setCsrfToken(undefined)
      setSession(null)
    } finally {
      setLoading(false)
    }
  }, [establish])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const logout = useCallback(async () => {
    try {
      await api<void>('/auth/logout', { method: 'POST' })
    } finally {
      setCsrfToken(undefined)
      setSession(null)
    }
  }, [])

  const switchTenant = useCallback(
    async (tenantId: string) => {
      await api('/auth/tenant', {
        method: 'POST',
        body: jsonBody({ tenantId }),
      })
      await refresh()
    },
    [refresh]
  )

  const exitPlatformAccess = useCallback(async () => {
    await api<void>('/platform/tenant-access', { method: 'DELETE' })
    await refresh()
  }, [refresh])

  const role =
    session?.platformAccess?.tenantId === session?.activeTenantId
      ? 'TENANT_ADMIN'
      : (session?.memberships.find(
          (membership) => membership.tenantId === session.activeTenantId
        )?.role ?? null)

  const value = useMemo(
    () => ({
      session,
      loading,
      role,
      refresh,
      establish,
      logout,
      switchTenant,
      exitPlatformAccess,
    }),
    [
      session,
      loading,
      role,
      refresh,
      establish,
      logout,
      switchTenant,
      exitPlatformAccess,
    ]
  )
  return <AuthContext value={value}>{children}</AuthContext>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider')
  }
  return context
}
